import { useState, useCallback, useEffect } from 'react';

import {
  loadRazorpayScript,
  initiateRazorpayPayment,
  initiateRazorpayTopUp,
} from './useRazorpayHelpers';


interface UseRazorpayOptions {
  onSuccess?: (response: { paymentId: string; credits: number }) => void;
  onFailure?: (error: string) => void;
  onDismiss?: () => void;
  prefill?: {
    name?: string;
    email?: string;
  };
}

interface UseRazorpayReturn {
  initiatePayment: (taskIds?: string[]) => Promise<void>;
  initiateTopUp: (creditAmount: number, currency?: string) => Promise<void>;
  isProcessing: boolean;
  isScriptLoaded: boolean;
  error: string | null;
}

const RAZORPAY_SCRIPT_URL = 'https://checkout.razorpay.com/v1/checkout.js';

const isRazorpayLoaded = (): boolean => typeof window !== 'undefined' && !!window.Razorpay;

const loadScriptIfNeeded = (
  setIsScriptLoaded: (loaded: boolean) => void,
  setError: (e: string | null) => void,
  onFailure?: (error: string) => void
): void => {
  if (isRazorpayLoaded()) {
    // Only set if not already true, to avoid unnecessary re-renders
    setIsScriptLoaded(true);

    return;
  }

  if (typeof window !== 'undefined') {
    loadRazorpayScript(RAZORPAY_SCRIPT_URL, setIsScriptLoaded, setError, onFailure);
  }
};

const useRazorpayScript = (
  setIsScriptLoaded: (loaded: boolean) => void,
  setError: (e: string | null) => void,
  onFailure?: (error: string) => void
): void => {
  useEffect(() => {
    loadScriptIfNeeded(setIsScriptLoaded, setError, onFailure);
  }, [onFailure, setIsScriptLoaded, setError]);
};

const usePaymentInitiator = (
  isScriptLoaded: boolean,
  setIsProcessing: (p: boolean) => void,
  setError: (e: string | null) => void,
  onSuccess?: (response: { paymentId: string; credits: number }) => void,
  onFailure?: (error: string) => void,
  onDismiss?: () => void,
  prefill?: { name?: string; email?: string }
) => useCallback(
  async (taskIds?: string[]) => {
    await initiateRazorpayPayment(
      isScriptLoaded,
      setIsProcessing,
      setError,
      window.Razorpay,
      taskIds,
      onSuccess,
      onFailure,
      prefill,
      onDismiss
    );
  },
  [isScriptLoaded, setIsProcessing, setError, onSuccess, onFailure, prefill, onDismiss]
);

const useTopUpInitiator = (
  isScriptLoaded: boolean,
  setIsProcessing: (p: boolean) => void,
  setError: (e: string | null) => void,
  onSuccess?: (response: { paymentId: string; credits: number }) => void,
  onFailure?: (error: string) => void,
  onDismiss?: () => void,
  prefill?: { name?: string; email?: string }
) => useCallback(
  async (creditAmount: number, currency: string = 'USD') => {
    await initiateRazorpayTopUp(
      isScriptLoaded,
      setIsProcessing,
      setError,
      window.Razorpay,
      creditAmount,
      currency,
      onSuccess,
      onFailure,
      prefill,
      onDismiss
    );
  },
  [isScriptLoaded, setIsProcessing, setError, onSuccess, onFailure, prefill, onDismiss]
);

const useRazorpayState = () => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  return { isProcessing, setIsProcessing, isScriptLoaded, setIsScriptLoaded, error, setError };
};

export const useRazorpay = (options: UseRazorpayOptions = {}): UseRazorpayReturn => {
  const { onSuccess, onFailure, onDismiss, prefill } = options;
  const { isProcessing, setIsProcessing, isScriptLoaded, setIsScriptLoaded, error, setError } = useRazorpayState();

  useRazorpayScript(setIsScriptLoaded, setError, onFailure);

  const initiatePayment = usePaymentInitiator(
    isScriptLoaded,
    setIsProcessing,
    setError,
    onSuccess,
    onFailure,
    onDismiss,
    prefill
  );

  const initiateTopUp = useTopUpInitiator(
    isScriptLoaded,
    setIsProcessing,
    setError,
    onSuccess,
    onFailure,
    onDismiss,
    prefill
  );

  return { initiatePayment, initiateTopUp, isProcessing, isScriptLoaded, error };
};
